import OrderInfoItem from 'components/info-items/OrderInfoItem';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Address {
  street?: string;
  city?: string;
  region?: string;
  postcode?: string;
  country?: string;
}

interface CustomerDetailsProps {
  customerDetails?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: Address;
  };
}

interface ShippingDetailsProps {
  shippingDetails?: {
    method?: string;
    date?: string;
    address?: Address;
  };
}

interface PaymentDetailsProps {
  paymentDetails?: {
    method?: string;
    status?: string;
    isGift?: boolean;
    wrapping?: string;
    message?: string;
  };
}

export const BillingDetails = ({ customerDetails }: CustomerDetailsProps) => {
  const { t } = useTranslation();
  const name = customerDetails?.name || t('common.notAvailable');
  const email = customerDetails?.email || t('common.notAvailable');
  const address = customerDetails?.address || {};
  const { street, city, region, postcode, country } = address;
  
  const formattedAddress = [street, city, region, postcode, country]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <h4 className="mb-4">{t('orderDelivery.billingDetails')}</h4>
      <Row className="g-3 flex-sm-column">
        <Col xs={6} sm={12}>
          <OrderInfoItem icon="user" label={t('orderDelivery.customer')} />
          <div className="fs-9 ms-4">
            {name}
          </div>
        </Col>
        <Col xs={6} sm={12}>
          <OrderInfoItem icon="mail" label={t('orderDelivery.email')} />
          {email !== t('common.notAvailable') ? (
            <Link className="fs-9 ms-4" to={`mailto:${email}`}>
              {email}
            </Link>
          ) : (
            <div className="fs-9 ms-4 text-body-secondary">{email}</div>
          )}
        </Col>
        {customerDetails?.phone && (
          <Col xs={6} sm={12}>
            <OrderInfoItem icon="phone" label={t('orderDelivery.phone')} />
            <Link className="fs-9 ms-4" to={`tel:${customerDetails.phone}`}>
              {customerDetails.phone}
            </Link>
          </Col>
        )}
        <Col xs={6} sm={12}>
          <OrderInfoItem icon="home" label={t('orderDelivery.address')} />
          <div className="ms-4">
            <p className="text-body-secondary mb-0 fs-9">{name}</p>
            <p className="text-body-secondary mb-0 fs-9">
              {formattedAddress || t('orderDelivery.addressNotAvailable')}
            </p>
          </div>
        </Col>
      </Row>
    </>
  );
};

export const ShippingDetails = ({ shippingDetails }: ShippingDetailsProps) => {
  const { t } = useTranslation();
  const method = shippingDetails?.method || t('orderDelivery.standardShipping');
  const date = shippingDetails?.date || new Date().toLocaleDateString();
  const address = shippingDetails?.address || {};
  const { street, city, region, postcode, country } = address;
  
  const formattedAddress = [street, city, region, postcode, country]
    .filter(Boolean)
    .join(', ');
  return (
    <>
      <h4 className="mb-4">{t('orderDelivery.shippingDetails')}</h4>
      <Row className="g-3 flex-sm-column">
        <Col xs={6} sm={12}>
          <OrderInfoItem icon="truck" label={t('orderDelivery.method')} />
          <p className="mb-0 text-body-secondary fs-9 ms-4">{method}</p>
        </Col>
        {/* <Col xs={6} sm={12}>
          <OrderInfoItem icon="calendar" label={t('orderDelivery.shippingDate')} />
          <p className="mb-0 text-body-secondary fs-9 ms-4">{date}</p>
        </Col> */}
        <Col xs={6} sm={12}>
          <OrderInfoItem icon="home" label={t('orderDelivery.address')} />
          <div className="ms-4">
            <p className="text-body-secondary mb-0 fs-9">
              {formattedAddress || t('orderDelivery.addressNotAvailable')}
            </p>
          </div>
        </Col>
      </Row>
    </>
  );
};

// Function to normalize status text for translation purposes
const normalizeStatusText = (statusText: string | undefined): string => {
  if (!statusText) return 'processing';
  
  // Convert to lowercase for consistent matching
  const lowerStatus = statusText.toLowerCase();
  
  // Map common French status terms to their English equivalents
  if (lowerStatus === 'terminée' || lowerStatus === 'terminee') {
    return 'completed';
  } else if (lowerStatus.includes('en cours') || lowerStatus.includes('processing')) {
    return 'processing';
  } else if (lowerStatus.includes('annul') || lowerStatus.includes('cancel')) {
    return 'canceled';
  } else if (lowerStatus.includes('prépar') || lowerStatus.includes('prepar')) {
    return 'being_prepared';
  } else if (lowerStatus === 'en attente' || lowerStatus === 'pending') {
    return 'pending';
  }
  
  // If no mapping found, return the original
  return lowerStatus;
};

export const OtherDetails = ({ paymentDetails }: PaymentDetailsProps) => {
  const { t } = useTranslation();
  
  // Handle payment method translation
  let methodDisplay = t('paymentMethod.creditCard');
  if (paymentDetails?.method) {
    const methodLower = paymentDetails.method.toLowerCase();
    if (methodLower.includes('cash') || methodLower.includes('livraison') || methodLower.includes('cod')) {
      methodDisplay = t('paymentMethod.cashOnDelivery');
    } else {
      methodDisplay = t('paymentMethod.creditCard');
    }
  }
  
  // Get normalized status key for translation
  const normalizedStatus = normalizeStatusText(paymentDetails?.status);
  // Translate the normalized status
  const statusDisplay = t(`orderStatus.${normalizedStatus}`);
  
  const isGift = paymentDetails?.isGift || false;
  const wrapping = paymentDetails?.wrapping || t('common.no');
  return (
    <>
      <h4 className="mb-4">{t('orderDelivery.paymentDetails')}</h4>
      <Row className="g-3 flex-sm-column">
        <Col xs={6} sm={12}>
          <OrderInfoItem icon="credit-card" label={t('orderDelivery.paymentMethod')} />
          <p className="mb-0 text-body-secondary fs-9 ms-4">{methodDisplay}</p>
        </Col>
        {/* <Col xs={6} sm={12}>
          <OrderInfoItem icon="file-text" label="Order Status" />
          <p className="mb-0 text-body-secondary fs-9 ms-4">{status}</p>
        </Col> */}
        {paymentDetails?.message && (
          <Col xs={6} sm={12}>
            <OrderInfoItem icon="mail" label={t('orderDelivery.notes')} />
            <div className="ms-4">
              <p className="text-body-secondary fs-9 mb-0">
                {paymentDetails.message}
              </p>
            </div>
          </Col>
        )}
      </Row>
    </>
  );
};
